import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, ShoppingBag, Loader2, AlertTriangle } from "lucide-react";

import OrderSummary from "../../../components/customer/CartComponents.tsx/OrderSummary";
import PaymentOptions from "../../../components/customer/PaymentOptions";

import { usePaymentInitiateMutation } from "../../../redux/actions/Customer/cartCustomer";

import { PaymentInitiateRequest } from "../../../types/Request/CustomerRequest";

import { PaymentSessionResponse } from "../../../types/Response/CustomerResponse";

const CustomerPaymentScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { orderId, pricing, cart, selectedAddress } = location.state || {};

  const [
    initiatePayment,
    { isLoading: isInitiatingPayment, isError: isGatewayError },
  ] = usePaymentInitiateMutation();

  const productItems = cart?.productCart?.items || [];

  const hasSubscribe = productItems.some(
    (item: any) => item.isSubscription === true,
  );

  useEffect(() => {
    if (!orderId) {
      navigate("/cartpage/customercartpage", {
        replace: true,
      });
      return;
    }

    // --------------------------------------------
    // Prevent opening payment page when there is
    // nothing left to pay.
    // --------------------------------------------

    if ((pricing?.gatewayAmount ?? 0) <= 0) {
      navigate("/cartpage/CustomerPaymentSuccessScreen", {
        replace: true,
        state: {
          orderId,
          pricing,
          selectedAddress,
          directInternalPayment: true,
        },
      });

      return;
    }
  }, [orderId, pricing, navigate]);

  const handlePaymentGatewayExecution = async (paymentMethodToken: string) => {
    try {
      const initiatePayload: PaymentInitiateRequest = {
        orderId: Number(orderId),

        paymentMethod: "NETWORK",

        currency: "AED",

        // Pay only remaining gateway amount
        amount: Number(pricing?.gatewayAmount ?? pricing?.totalAmount ?? 0),
      };

      const result: PaymentSessionResponse =
        await initiatePayment(initiatePayload).unwrap();

      if (result?.data?.paymentUrl && result?.data?.transactionReference) {
        sessionStorage.setItem("checkout_order_id", String(orderId));

        sessionStorage.setItem(
          "checkout_tx_ref",
          result.data.transactionReference,
        );

        sessionStorage.setItem("checkout_pricing", JSON.stringify(pricing));

        sessionStorage.setItem(
          "checkout_address",
          JSON.stringify(selectedAddress),
        );

        window.location.href = result.data.paymentUrl;
      } else if (
        paymentMethodToken.toUpperCase() === "COD" &&
        result?.success
      ) {
        sessionStorage.setItem("checkout_order_id", String(orderId));

        navigate("/CustomerPaymentSuccessScreen", {
          state: {
            directCodSuccess: true,
          },
        });
      } else {
        alert(result?.data?.message ?? "Could not initialize secure payment.");
      }
    } catch (err) {
      console.error("Secure payment initialization failed:", err);
    }
  };

  return (
    <div className="px-4 lg:px-20 py-4 max-w-7xl mx-auto text-left selection:bg-[#A8D97B]/30 relative">
      {isInitiatingPayment && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex flex-col items-center justify-center gap-2 text-white text-xs font-black tracking-wide">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
          <span>Redirecting to secure checkout portal...</span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        {/* Left */}

        <div className="w-full lg:w-[60%] space-y-4">
          <div className="flex items-center gap-2 text-logoHeading font-black text-sm pl-1">
            <CreditCard size={16} className="text-[#2F5E2E] stroke-[2.5]" />
            <h2>Select Secure Payment Gateway</h2>
          </div>

          {hasSubscribe && (
            <div className="p-3.5 bg-amber-50 border border-dashed border-amber-200 text-amber-800 text-xs font-semibold rounded-xl">
              ⚠️{" "}
              <span className="font-black">
                Subscription products require Card payment.
              </span>
            </div>
          )}

          {isGatewayError && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-xl flex items-center gap-2">
              <AlertTriangle size={14} />
              <span>Payment initialization failed. Please try again.</span>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-3xs">
            <PaymentOptions
              onlyCard={hasSubscribe}
              onSuccess={(methodToken: string) =>
                handlePaymentGatewayExecution(methodToken || "NETWORK")
              }
              amount={pricing?.gatewayAmount ?? pricing?.totalAmount ?? 0}
              addressDetails={selectedAddress}
            />
          </div>
        </div>

        {/* Right */}

        <div className="w-full lg:w-[40%] space-y-4">
          <div className="flex items-center gap-2 text-slate-400 font-black text-xs uppercase tracking-wider pl-1">
            <ShoppingBag size={13} />
            <h2>Final Cost Summary</h2>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-3xs overflow-hidden">
            <OrderSummary
              totalMrp={pricing?.subTotal || 0}
              couponDiscount={pricing?.couponDiscount || 0}
              rewardDiscount={pricing?.rewardPointDiscount || 0}
              walletDiscount={pricing?.walletDiscount || 0}
              platformFee={pricing?.platformFee || 0}
              smallOrderFee={pricing?.smallOrderFee || 0}
              deliveryFee={pricing?.deliveryFee || 0}
              donation={pricing?.donationAmount || 0}
              taxAmount={pricing?.taxAmount || 0}
              grandTotal={pricing?.totalAmount || 0}
              gatewayAmount={
                pricing?.gatewayAmount ?? pricing?.totalAmount ?? 0
              }
              showButton={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentScreen;
